import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Platform, Pressable, StyleSheet, View } from "react-native";
import type { ApolloCache } from "@apollo/client";
import {
  ExternalVisibility,
  GetModernizedCourseDocument,
  GetModernizedCoursePublishHistoryDocument,
  ModernizedActivityEntityType,
  ModernizedActivityStatus,
  ModernizedDescriptionFormat,
  ModernizedCourseExportFormat,
  type GetModernizedCoursePublishHistoryQuery,
  type CreateIleInput,
  type IleObjectiveInput,
  type ObjectiveInput,
  type UpdateAiTutorInput,
  type VideoDataInput,
  type UpdateModernizedActivityFieldsMutationVariables,
  useClearModernizedCoursePublishHistoryMutation,
  useCreateIleForModernizedRecommendationMutation,
  useCreateModernizedModuleMutation,
  useGetModernizedCoursePublishHistoryQuery,
  usePublishModernizedActivitiesMutation,
  useRequestModernizedCourseExportMutation,
  useUpdateAiTutorMutation,
  useUpdateModernizedActivityFieldsMutation,
} from "@axio/graphql/generated/graphql";

import CustomAdminModal from "../../../../components/common/general/CustomAdminModal/CustomAdminModal";
import CustomText from "../../../../components/common/general/CustomText/CustomText";
import { useToast } from "../../../../contexts/Toast/ToastProvider";

import ReviewPanel from "./ReviewPanel";
import ModernizerAddModuleModal, { type ModernizerAddModuleInput } from "./components/ModernizerAddModuleModal";
import ModernizerIleCreationSheet from "./components/ModernizerIleCreationSheet";
import { COURSE_EVALUATOR_REVIEW_ITEM_TYPE_LABELS } from "./constants/itemTypeLabels";
import type { ActivityDataInput } from "../../../../components/customCoursework/ActivityForms/InteractiveLearningForm/InteractiveLearningFormCore";
import type {
  CourseEvaluatorReviewDecision,
  CourseEvaluatorReviewData,
  CourseEvaluatorReviewItemType,
  CourseEvaluatorReviewSidebarGroup,
  CourseEvaluatorReviewSidebarItem,
} from "./types/reviewTypes";
import type {
  ActivityDetail,
  PublishedPublishItem,
  PublishDocumentUpdate,
  PublishManualStep,
  PublishStatus,
  QueuedPublishItem,
  StructuredFields,
} from "./types/reviewTypes";
import {
  buildTriageQueryState,
  parseTriageQueryState,
  type TriageDimension,
  type TriageQueryState,
  type TriageSurface,
} from "./triageQueryParams";
import { getManagedFileDownloadAction, submitAuthenticatedHiddenDownloadForm } from "./utils/webFileDownload";

interface DecisionEntry {
  decision: CourseEvaluatorReviewDecision;
}

function getSidebarItemType(item: CourseEvaluatorReviewSidebarItem): CourseEvaluatorReviewItemType | undefined {
  const itemTypeMeta = item.meta?.find(
    (meta): meta is { kind: "itemType"; itemType: CourseEvaluatorReviewItemType } => meta.kind === "itemType"
  );
  return itemTypeMeta?.itemType;
}

function getSidebarItemIsNew(item: CourseEvaluatorReviewSidebarItem): boolean {
  return Boolean(item.meta?.some((meta) => meta.kind === "tag" && meta.tag === "new"));
}

function getItemTypeLabel(itemType: CourseEvaluatorReviewItemType): string {
  if (itemType === "file") {
    return "File";
  }

  if (itemType === "link") {
    return "External URL";
  }

  return COURSE_EVALUATOR_REVIEW_ITEM_TYPE_LABELS[itemType];
}

/** Map sidebar item kind to the category label shown in the publish sheet. */
function getCategoryLabel(item: CourseEvaluatorReviewSidebarItem): string {
  const itemType = getSidebarItemType(item);
  const isNew = getSidebarItemIsNew(item);

  if (item.entityType === ModernizedActivityEntityType.Assessment || itemType === "quiz") {
    return "Quiz";
  }

  switch (item.entityType) {
    case ModernizedActivityEntityType.Assignment:
      if (itemType === "checklist" || itemType === "file" || itemType === "link") {
        return getItemTypeLabel(itemType);
      }
      return "Assignment";
    case ModernizedActivityEntityType.Module:
      return "Module Description";
    case ModernizedActivityEntityType.ContentSection:
      return "Course Content";
    case ModernizedActivityEntityType.Project:
      return isNew ? "Page" : "Assignment";
    case ModernizedActivityEntityType.OtherActivity:
      if (itemType === "file") {
        return "File Topic";
      }
      if (itemType) {
        return getItemTypeLabel(itemType);
      }
      return "Activity";
    case ModernizedActivityEntityType.IleRecommendation:
      return "Axio ILE";
    case ModernizedActivityEntityType.Assessment:
      return "Quiz";
  }

  switch (item.kind) {
    case "moduleDescription":
      return "Module Description";
    case "recommendation":
      return "Recommendation";
    case "assignment":
      return "Assignment";
    case "suggestedContent":
      return "Suggested Content";
    default:
      return "Change";
  }
}

interface UndoEntry {
  itemId: string;
  previous: DecisionEntry;
}

interface CourseEvaluatorReviewContainerProps {
  review: CourseEvaluatorReviewData;
  activityDetails: Record<string, ActivityDetail>;
  onExitReview: () => void;
  initialQueryState?: TriageQueryState;
}

type ModernizedActivityCacheTypename =
  | "ModernizedAssignment"
  | "ModernizedModule"
  | "ModernizedOtherActivity"
  | "ModernizedCourseContentSection"
  | "ModernizedAssessment"
  | "ModernizedProject"
  | "ILERecommendation";

type PublishHistoryRecord =
  GetModernizedCoursePublishHistoryQuery["getModernizedCoursePublishHistory"]["records"][number];
type PublishHistoryItem = PublishHistoryRecord["autoPublishedItems"][number];
type PublishHistoryManualStep = PublishHistoryRecord["manualSteps"][number];

function mapPublishHistoryItem(item: PublishHistoryItem): PublishedPublishItem {
  return {
    id: item.id,
    title: item.title,
    category: item.category,
    moduleName: item.moduleName ?? "Course-Level Content",
  };
}

function mapPublishHistoryManualStep(step: PublishHistoryManualStep): PublishManualStep {
  return {
    id: step.id,
    title: step.title,
    action: step.action,
    detail: step.detail,
    externalUrl: step.externalUrl,
    downloadUrl: step.downloadUrl,
  };
}

function getActivityCacheTypename(entityType: ModernizedActivityEntityType): ModernizedActivityCacheTypename {
  switch (entityType) {
    case ModernizedActivityEntityType.Assignment:
      return "ModernizedAssignment";
    case ModernizedActivityEntityType.Module:
      return "ModernizedModule";
    case ModernizedActivityEntityType.OtherActivity:
      return "ModernizedOtherActivity";
    case ModernizedActivityEntityType.ContentSection:
      return "ModernizedCourseContentSection";
    case ModernizedActivityEntityType.Assessment:
      return "ModernizedAssessment";
    case ModernizedActivityEntityType.Project:
      return "ModernizedProject";
    case ModernizedActivityEntityType.IleRecommendation:
      return "ILERecommendation";
  }
}

function updateActivityStatusInCache<TSerialized>(
  cache: ApolloCache<TSerialized>,
  item: CourseEvaluatorReviewSidebarItem,
  status: ModernizedActivityStatus
): void {
  const cacheId = cache.identify({
    __typename: getActivityCacheTypename(item.entityType),
    id: item.id,
  });

  if (!cacheId) {
    return;
  }

  cache.modify({
    id: cacheId,
    fields: {
      status: () => status,
    },
  });
}

function getDecisionFromStatus(status: ModernizedActivityStatus | null | undefined): CourseEvaluatorReviewDecision {
  switch (status) {
    case ModernizedActivityStatus.Queued:
      return "accepted";
    case ModernizedActivityStatus.Skipped:
      return "rejected";
    case ModernizedActivityStatus.Published:
      return "synced";
    default:
      return null;
  }
}

function getStatusFromDecision(decision: CourseEvaluatorReviewDecision): ModernizedActivityStatus {
  switch (decision) {
    case "accepted":
    case "edited":
      return ModernizedActivityStatus.Queued;
    case "rejected":
      return ModernizedActivityStatus.Skipped;
    case "synced":
      return ModernizedActivityStatus.Published;
    default:
      return ModernizedActivityStatus.Undecided;
  }
}

function buildInitialDecisions(sidebarGroups: CourseEvaluatorReviewSidebarGroup[]): Map<string, DecisionEntry> {
  const initialDecisions = new Map<string, DecisionEntry>();

  sidebarGroups.forEach((group) => {
    group.items.forEach((item) => {
      const decision = getDecisionFromStatus(item.status);
      if (decision) {
        initialDecisions.set(item.id, { decision });
      }
    });
  });

  return initialDecisions;
}

function getDecisionSourceSignature(sidebarGroups: CourseEvaluatorReviewSidebarGroup[]): string {
  return sidebarGroups
    .map((group) => `${group.id}:${group.items.map((item) => `${item.id}:${item.status ?? ""}`).join(",")}`)
    .join("|");
}

/**
 * Converts a user-entered date string to an ISO 8601 string for the GraphQL
 * DateTime scalar, preserving a tri-state:
 * - `undefined` → field not present/shown → leave untouched
 * - `null`       → user explicitly cleared → send null to backend
 * - `string`     → user set a date → parse and return ISO string (or null if unparseable)
 *
 * Uses UTC-based parsing to avoid timezone-dependent date shifts.
 */
function parseDateToIso(value: string | null | undefined): string | null | undefined {
  if (value === undefined) return undefined;
  if (!value.trim()) return null;
  // Parse as a UTC midnight date to avoid local-timezone shifts on date-only strings.
  const match = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (match) {
    const [, month, day, year] = match;
    const d = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
    return isNaN(d.getTime()) ? null : d.toISOString();
  }
  // Fall back to native parsing for ISO strings already in the correct format.
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

function getEditMutationVariables(
  item: CourseEvaluatorReviewSidebarItem,
  editedFields: StructuredFields
): UpdateModernizedActivityFieldsMutationVariables | null {
  const itemType = getSidebarItemType(item);
  const recommendedDescriptionFormat =
    editedFields.description === undefined
      ? undefined
      : itemType === "webpage"
        ? ModernizedDescriptionFormat.Html
        : ModernizedDescriptionFormat.Markdown;

  switch (item.entityType) {
    case ModernizedActivityEntityType.Assignment:
      return {
        entityId: item.id,
        entityType: item.entityType,
        recommendedTitle: editedFields.title,
        recommendedDescription: editedFields.description,
        recommendedDescriptionFormat,
        recommendedPointsPossible: editedFields.points,
        recommendedDueDate: parseDateToIso(editedFields.dueDate),
      };
    case ModernizedActivityEntityType.Module:
      return {
        entityId: item.id,
        entityType: item.entityType,
        recommendedTitle: editedFields.title,
        recommendedDescription: editedFields.description,
        recommendedDescriptionFormat,
        // Modules do not support due dates; omit recommendedDueDate entirely.
      };
    case ModernizedActivityEntityType.OtherActivity:
      return {
        entityId: item.id,
        entityType: item.entityType,
        recommendedTitle: editedFields.title,
        recommendedDescription: editedFields.description,
        recommendedDescriptionFormat,
        recommendedDueDate: parseDateToIso(editedFields.dueDate),
      };
    case ModernizedActivityEntityType.Project:
      return {
        entityId: item.id,
        entityType: item.entityType,
        recommendedTitle: editedFields.title,
        recommendedDescription: editedFields.description,
        recommendedDescriptionFormat,
        recommendedDueDate: parseDateToIso(editedFields.dueDate),
      };
    case ModernizedActivityEntityType.ContentSection:
      return {
        entityId: item.id,
        entityType: item.entityType,
        recommendedValue: editedFields.description,
      };
    case ModernizedActivityEntityType.Assessment:
      return {
        entityId: item.id,
        entityType: item.entityType,
        recommendedTitle: editedFields.title,
        recommendedDescription: editedFields.description,
        recommendedPointsPossible: editedFields.points,
        recommendedDueDate: parseDateToIso(editedFields.dueDate),
      };
    case ModernizedActivityEntityType.IleRecommendation:
      return null;
  }

  return null;
}

function isIleRecommendationItem(item: CourseEvaluatorReviewSidebarItem | null): boolean {
  return item?.entityType === ModernizedActivityEntityType.IleRecommendation;
}

function isExistingImportedIleItem(
  item: CourseEvaluatorReviewSidebarItem | null,
  detail: ActivityDetail | null | undefined
): boolean {
  return (
    isIleRecommendationItem(item) &&
    item !== null &&
    !getSidebarItemIsNew(item) &&
    Boolean(detail?.createdIleId) &&
    detail?.createdIleWasUserCreated !== true
  );
}

function mapObjectives(objectives: ActivityDataInput["objectives"] = []): IleObjectiveInput[] {
  return objectives.map((objective, index) => ({
    objective: objective.objective,
    sequence: objective.sequence ?? index + 1,
    evalCriteria: objective.evalCriteria || [],
  }));
}

function mapObjectivesToUpdateInput(objectives: ActivityDataInput["objectives"] = []): ObjectiveInput[] {
  return objectives.map((objective, index) => ({
    id: objective.id,
    objective: objective.objective,
    sequence: objective.sequence ?? index + 1,
    evalCriteria: objective.evalCriteria || [],
  }));
}

function mapVideoPool(videoPool: ActivityDataInput["videoPool"] = []): VideoDataInput[] {
  return videoPool.map((video) => ({
    videoId: video.videoId,
    objectiveText: video.objectiveText ?? "",
    objectiveSequence: video.objectiveSequence ?? 1,
    isTeacherAdded: Boolean(video.isTeacherAdded),
  }));
}

function normalizeDueDate(raw: string | Date | null | undefined): string | null {
  if (!raw) return null;
  const date = raw instanceof Date ? raw : new Date(raw);
  if (Number.isNaN(date.getTime())) return null;
  const yyyy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(date.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T12:00:00.000Z`;
}

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

function getMissingIleBulkWarningMessage(count: number): string {
  return count === 1
    ? "Create the ILE before adding this recommendation to the queue."
    : `Create ILEs before adding these ${count} recommendations to the queue.`;
}

function mapActivityDataToCreateIleInput(activityData: ActivityDataInput): CreateIleInput {
  const dueDate = normalizeDueDate(activityData.dueDate);
  return {
    title: activityData.title ?? "Untitled ILE",
    description: activityData.description ?? "",
    context: activityData.context ?? "",
    objectives: mapObjectives(activityData.objectives ?? activityData.rawObjectives),
    videoPool: mapVideoPool(activityData.videoPool),
    ...(dueDate ? { dueDate } : {}),
  };
}

function mapActivityDataToUpdateIleInput(activityData: ActivityDataInput): UpdateAiTutorInput {
  const dueDate = normalizeDueDate(activityData.dueDate);
  const updateInput: UpdateAiTutorInput = {
    title: activityData.title ?? "Untitled ILE",
    description: activityData.description ?? "",
    context: activityData.context ?? "",
    targetAudience: activityData.targetAudience ?? "",
    objectives: mapObjectivesToUpdateInput(activityData.objectives ?? activityData.rawObjectives),
    dueDate,
    activityState: activityData.activityState,
    scheduledDate: activityData.scheduledDate,
    visualizationId: activityData.visualizationId ?? null,
  };

  if (activityData.videoPool) {
    updateInput.videoPool = mapVideoPool(activityData.videoPool);
  }

  if (activityData.imagePool) {
    updateInput.imagePool = activityData.imagePool.map((image) => ({
      id: image.id,
      url: image.url,
      objectiveText: image.objectiveText,
      objectiveSequence: image.objectiveSequence,
      isTeacherUploaded: Boolean(image.isTeacherUploaded),
    }));
  }

  return updateInput;
}

function getActiveSurfaceForTriage(itemId: string | null, dimension: TriageDimension | null): TriageSurface {
  if (itemId) {
    return "entity";
  }

  if (dimension) {
    return "dimension";
  }

  return "overview";
}

function CourseEvaluatorReviewContainer({
  review,
  activityDetails,
  onExitReview,
  initialQueryState,
}: CourseEvaluatorReviewContainerProps) {
  const { showToast } = useToast();
  const [publishModernizedActivitiesMutation] = usePublishModernizedActivitiesMutation();
  const [requestModernizedCourseExportMutation] = useRequestModernizedCourseExportMutation();
  const [updateModernizedActivityFieldsMutation] = useUpdateModernizedActivityFieldsMutation();
  const [createIleForModernizedRecommendationMutation, { loading: isCreatingIle }] =
    useCreateIleForModernizedRecommendationMutation();
  const [createModernizedModuleMutation, { loading: isCreatingModernizedModule }] = useCreateModernizedModuleMutation();
  const [updateAiTutorMutation, { loading: isUpdatingIle }] = useUpdateAiTutorMutation();
  const [clearModernizedCoursePublishHistoryMutation, { loading: isClearingPublishHistory }] =
    useClearModernizedCoursePublishHistoryMutation();
  const { data: publishHistoryData, refetch: refetchPublishHistory } = useGetModernizedCoursePublishHistoryQuery({
    variables: { courseId: review.courseId },
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });
  const initialSelectedItemIdCandidate = initialQueryState?.sheet === "open" ? initialQueryState.entityId : null;
  const validInitialItemIds = new Set(review.sidebarGroups.flatMap((group) => group.items.map((item) => item.id)));
  const initialSelectedItemId =
    initialSelectedItemIdCandidate && validInitialItemIds.has(initialSelectedItemIdCandidate)
      ? initialSelectedItemIdCandidate
      : null;
  const [selectedItemId, setSelectedItemId] = useState<string | null>(initialSelectedItemId);
  // Dimension-driven navigation is not fully interactive in this container yet, so
  // only hydrate entity-driven selection from the URL. This avoids landing in a
  // dimension-focused state that the user cannot subsequently change or clear here.
  const [selectedDimension, setSelectedDimension] = useState<TriageDimension | null>(null);
  const [activeSurface, setActiveSurface] = useState<TriageSurface>(() =>
    getActiveSurfaceForTriage(initialSelectedItemId, null)
  );
  const [filterMode, setFilterMode] = useState<"all" | "undecided">("all");
  const [collapsedSectionIds, setCollapsedSectionIds] = useState<Set<string>>(
    () => new Set(review.sidebarGroups.filter((group) => !group.defaultExpanded).map((group) => group.id))
  );
  const [decisions, setDecisions] = useState<Map<string, DecisionEntry>>(() =>
    buildInitialDecisions(review.sidebarGroups)
  );
  const [undoEntry, setUndoEntry] = useState<UndoEntry | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishSheetOpen, setIsPublishSheetOpen] = useState(false);
  const [isPublishHistoryOpen, setIsPublishHistoryOpen] = useState(false);
  const [isAddModuleModalOpen, setIsAddModuleModalOpen] = useState(false);
  const [pendingCreatedModuleId, setPendingCreatedModuleId] = useState<string | null>(null);
  const [ileCreationItemId, setIleCreationItemId] = useState<string | null>(null);
  const [isLivePublishWarningVisible, setIsLivePublishWarningVisible] = useState(false);
  const [publishStatus, setPublishStatus] = useState<PublishStatus>("idle");
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [isExportingImscc, setIsExportingImscc] = useState(false);
  const [liveActivityDetails, setLiveActivityDetails] = useState<Record<string, ActivityDetail>>(activityDetails);
  /** Publish history record shown in the success state, captured after publish refetch. */
  const [lastPublishRecord, setLastPublishRecord] = useState<PublishHistoryRecord | null>(null);
  const [lastPublishedFallbackItems, setLastPublishedFallbackItems] = useState<PublishedPublishItem[]>([]);
  const decisionSourceSignature = useMemo(
    () => getDecisionSourceSignature(review.sidebarGroups),
    [review.sidebarGroups]
  );

  // Ref to track latest decisions for non-blocking auto-advance
  const decisionsRef = useRef(decisions);
  decisionsRef.current = decisions;

  const isMountedRef = useRef(true);
  const decisionSourceSignatureRef = useRef(decisionSourceSignature);
  const nextUrlWriteModeRef = useRef<"push" | "replace">("replace");
  const isApplyingPopstateRef = useRef(false);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (decisionSourceSignatureRef.current === decisionSourceSignature) {
      return;
    }

    decisionSourceSignatureRef.current = decisionSourceSignature;
    setDecisions(buildInitialDecisions(review.sidebarGroups));
  }, [decisionSourceSignature, review.sidebarGroups]);

  useEffect(() => {
    setLiveActivityDetails(activityDetails);
  }, [activityDetails]);

  const allItems = useMemo(() => review.sidebarGroups.flatMap((group) => group.items), [review.sidebarGroups]);

  const orderedIds = useMemo(() => allItems.map((item) => item.id), [allItems]);
  const validItemIds = useMemo(() => new Set(orderedIds), [orderedIds]);

  const setSelectedItemForTriage = useCallback(
    (itemId: string | null, mode: "push" | "replace" = "push") => {
      nextUrlWriteModeRef.current = mode;
      setSelectedItemId(itemId);
      setActiveSurface(getActiveSurfaceForTriage(itemId, selectedDimension));
    },
    [selectedDimension]
  );

  const writeTriageQueryToUrl = useCallback(
    (mode: "push" | "replace") => {
      if (Platform.OS !== "web" || typeof window === "undefined") {
        return;
      }

      const nextSearch = buildTriageQueryState(window.location.search, {
        courseId: review.courseId,
        dimension: selectedDimension,
        entityId: selectedItemId,
        surface: activeSurface,
        sheet: selectedItemId ? "open" : "closed",
      });

      if (nextSearch === window.location.search) {
        return;
      }

      const nextUrl = `${window.location.pathname}${nextSearch}${window.location.hash}`;
      if (mode === "push") {
        window.history.pushState({}, "", nextUrl);
        return;
      }

      window.history.replaceState({}, "", nextUrl);
    },
    [activeSurface, review.courseId, selectedDimension, selectedItemId]
  );

  useEffect(() => {
    if (selectedItemId && !validItemIds.has(selectedItemId)) {
      // If popstate landed on an invalid entity, allow the normalization write.
      if (isApplyingPopstateRef.current) {
        isApplyingPopstateRef.current = false;
      }
      setSelectedItemForTriage(null, "replace");
    }
  }, [selectedItemId, setSelectedItemForTriage, validItemIds]);

  useEffect(() => {
    if (!pendingCreatedModuleId || !validItemIds.has(pendingCreatedModuleId)) {
      return;
    }

    setSelectedItemForTriage(pendingCreatedModuleId, "replace");
    setPendingCreatedModuleId(null);
  }, [pendingCreatedModuleId, setSelectedItemForTriage, validItemIds]);

  useEffect(() => {
    if (Platform.OS !== "web" || typeof window === "undefined") {
      return;
    }

    if (isApplyingPopstateRef.current) {
      isApplyingPopstateRef.current = false;
      return;
    }

    const mode = nextUrlWriteModeRef.current;
    nextUrlWriteModeRef.current = "replace";
    writeTriageQueryToUrl(mode);
  }, [writeTriageQueryToUrl]);

  useEffect(() => {
    if (Platform.OS !== "web" || typeof window === "undefined") {
      return;
    }

    const handlePopstate = () => {
      const queryState = parseTriageQueryState(window.location.search);
      isApplyingPopstateRef.current = true;
      setSelectedDimension(queryState.dimension);
      setActiveSurface(queryState.surface);
      setSelectedItemId(queryState.sheet === "open" ? queryState.entityId : null);
    };

    window.addEventListener("popstate", handlePopstate);
    return () => window.removeEventListener("popstate", handlePopstate);
  }, []);

  /** Lookup table mapping each item id to its enclosing module info. */
  const itemModuleIndex = useMemo(() => {
    const map = new Map<string, { moduleId: string | null; moduleName: string; moduleExternalUrl?: string }>();
    review.sidebarGroups.forEach((group) => {
      const moduleId = group.id === "course-level-content" ? null : group.id;
      group.items.forEach((item) => {
        map.set(item.id, { moduleId, moduleName: group.title, moduleExternalUrl: group.externalUrl });
      });
    });
    return map;
  }, [review.sidebarGroups]);

  const liveSidebarGroups = useMemo<CourseEvaluatorReviewSidebarGroup[]>(() => {
    return review.sidebarGroups
      .map((group) => {
        const decided = group.items.filter((item) => decisions.get(item.id)?.decision !== undefined).length;
        const items =
          filterMode === "undecided"
            ? group.items.filter((item) => decisions.get(item.id)?.decision === undefined)
            : group.items;

        return { ...group, decided, items };
      })
      .filter((group) => filterMode === "all" || group.items.length > 0);
  }, [review.sidebarGroups, filterMode, decisions]);

  const selectedSidebarItem = useMemo<CourseEvaluatorReviewSidebarItem | null>(() => {
    if (!selectedItemId) return null;
    return allItems.find((item) => item.id === selectedItemId) || null;
  }, [allItems, selectedItemId]);

  const getDecision = useCallback(
    (id: string): CourseEvaluatorReviewDecision => decisions.get(id)?.decision ?? null,
    [decisions]
  );

  const selectedDecision = selectedItemId ? getDecision(selectedItemId) : null;
  const selectedItemRequiresIleCreation = isIleRecommendationItem(selectedSidebarItem);
  const selectedItemDetail = selectedItemId ? liveActivityDetails[selectedItemId] : null;
  const selectedItemHasCreatedIle = Boolean(selectedItemDetail?.createdIleId);
  const selectedItemIsExistingImportedIle = isExistingImportedIleItem(selectedSidebarItem, selectedItemDetail);
  const canAcceptSelectedItem =
    !selectedItemRequiresIleCreation || (!selectedItemIsExistingImportedIle && selectedItemHasCreatedIle);
  const acceptHiddenReason = selectedItemIsExistingImportedIle
    ? "Existing ILE edits are saved directly and do not need Brightspace publish."
    : undefined;
  const acceptDisabledReason =
    selectedItemRequiresIleCreation && !selectedItemHasCreatedIle
      ? "Create the ILE before adding it to queue."
      : undefined;

  const ileCreationDetail = ileCreationItemId ? liveActivityDetails[ileCreationItemId] : null;
  const ileCreationInitialValues = useMemo<ActivityDataInput>(() => {
    if (!ileCreationDetail) {
      return {};
    }

    const createdObjectives = ileCreationDetail.createdIleObjectives ?? [];
    const seedObjective =
      ileCreationDetail.learningObjective ??
      ileCreationDetail.recommendedDescription ??
      ileCreationDetail.recommendedTitle ??
      ileCreationDetail.currentTitle ??
      "Complete the interactive learning experience.";
    const seedCriteria = [ileCreationDetail.recommendedDescription, ileCreationDetail.feedback]
      .filter((value): value is string => Boolean(value?.trim()))
      .filter((value, index, values) => values.indexOf(value) === index && value !== seedObjective);

    return {
      id: ileCreationDetail.createdIleId,
      title: ileCreationDetail.createdIleTitle ?? ileCreationDetail.recommendedTitle ?? ileCreationDetail.currentTitle,
      description:
        ileCreationDetail.createdIleDescription ??
        ileCreationDetail.recommendedDescription ??
        ileCreationDetail.currentDescription,
      context:
        ileCreationDetail.createdIleContext ??
        [ileCreationDetail.documentReference, ileCreationDetail.feedback].filter(Boolean).join("\n\n"),
      dueDate: ileCreationDetail.createdIleDueDate ?? null,
      targetAudience: ileCreationDetail.createdIleTargetAudience ?? undefined,
      activityState: ileCreationDetail.createdIleActivityState ?? undefined,
      scheduledDate: ileCreationDetail.createdIleScheduledDate ?? undefined,
      objectives:
        createdObjectives.length > 0
          ? createdObjectives.map((objective, index) => ({
              id: objective.id,
              objective: objective.objective,
              sequence: objective.sequence ?? index + 1,
              evalCriteria: objective.evalCriteria ?? [],
            }))
          : [
              {
                objective: seedObjective,
                sequence: 1,
                evalCriteria: seedCriteria.length > 0 ? seedCriteria : [seedObjective],
              },
            ],
    };
  }, [ileCreationDetail]);

  // --- Stats ---
  const stats = useMemo(() => {
    let accepted = 0;
    let rejected = 0;
    let published = 0;
    allItems.forEach((item) => {
      const d = decisions.get(item.id)?.decision;
      if ((d === "accepted" || d === "edited") && !isExistingImportedIleItem(item, liveActivityDetails[item.id])) {
        accepted++;
      }
      if (d === "rejected") rejected++;
      if (d === "synced") published++;
    });
    const decided = accepted + rejected + published;
    return { decided, accepted, rejected, published, total: allItems.length };
  }, [allItems, decisions, liveActivityDetails]);

  const reviewedProgress = stats.total > 0 ? (stats.decided / stats.total) * 100 : 0;

  const publishHistoryRecords = useMemo<PublishHistoryRecord[]>(() => {
    const result = publishHistoryData?.getModernizedCoursePublishHistory;
    if (!result?.success) {
      return [];
    }

    return result.records;
  }, [publishHistoryData]);

  const pendingManualStepsCount = useMemo(
    () =>
      publishHistoryRecords.reduce(
        (sum, record) => sum + Math.max(record.manualStepCount - record.completedManualStepCount, 0),
        0
      ),
    [publishHistoryRecords]
  );

  // --- Queued items for publish sheet ---
  const queuedPublishItems = useMemo<QueuedPublishItem[]>(() => {
    return allItems
      .filter((item) => {
        const d = decisions.get(item.id)?.decision;
        if (isExistingImportedIleItem(item, liveActivityDetails[item.id])) {
          return false;
        }
        return d === "accepted" || d === "edited";
      })
      .map((item) => {
        const mod = itemModuleIndex.get(item.id);
        const detail = liveActivityDetails[item.id];
        const moduleName =
          mod?.moduleId != null
            ? (liveActivityDetails[mod.moduleId]?.recommendedTitle ?? mod.moduleName)
            : (mod?.moduleName ?? "Course-Level Content");
        return {
          id: item.id,
          title: detail?.recommendedTitle ?? item.title,
          kind: item.kind,
          entityType: item.entityType,
          itemType: getSidebarItemType(item),
          isNew: getSidebarItemIsNew(item),
          category: getCategoryLabel(item),
          moduleId: mod?.moduleId ?? null,
          moduleName,
          moduleExternalUrl: mod?.moduleExternalUrl,
          externalUrl: detail?.url,
          attachments: detail?.attachments,
          wasEdited: decisions.get(item.id)?.decision === "edited",
          externalVisibility: item.externalVisibility,
        };
      });
  }, [allItems, decisions, itemModuleIndex, liveActivityDetails]);

  // Override review data with live stats + filtered sidebar
  const liveReview = useMemo<CourseEvaluatorReviewData>(
    () => ({
      ...review,
      reviewedCount: stats.decided,
      queuedCount: stats.accepted,
      skippedCount: stats.rejected,
      publishedCount: stats.published,
      sidebarGroups: liveSidebarGroups,
    }),
    [review, stats, liveSidebarGroups]
  );

  // --- Non-blocking auto-advance ---
  const advanceToNextUndecided = useCallback(
    (fromId: string) => {
      // Use requestAnimationFrame to keep the UI responsive
      requestAnimationFrame(() => {
        if (!isMountedRef.current) return;

        const currentDecisions = decisionsRef.current;
        const currentIdx = orderedIds.indexOf(fromId);

        // Search forward first
        for (let i = currentIdx + 1; i < orderedIds.length; i++) {
          const id = orderedIds[i];
          if (!currentDecisions.get(id)?.decision) {
            setSelectedItemForTriage(id);
            return;
          }
        }
        // Wrap around
        for (let i = 0; i < currentIdx; i++) {
          const id = orderedIds[i];
          if (!currentDecisions.get(id)?.decision) {
            setSelectedItemForTriage(id);
            return;
          }
        }
        // All decided — stay on current or go to overview
      });
    },
    [orderedIds]
  );

  const persistDecision = useCallback(
    async (item: CourseEvaluatorReviewSidebarItem, decision: CourseEvaluatorReviewDecision) => {
      const status = getStatusFromDecision(decision);
      const result = await updateModernizedActivityFieldsMutation({
        variables: {
          entityId: item.id,
          entityType: item.entityType,
          status,
        },
        optimisticResponse: {
          updateModernizedActivityFields: {
            __typename: "ModernizedEditLogResult",
            success: true,
          },
        },
        update: (cache) => updateActivityStatusInCache(cache, item, status),
      });

      if (!result.data?.updateModernizedActivityFields.success) {
        throw new Error("updateModernizedActivityFields mutation failed");
      }
    },
    [updateModernizedActivityFieldsMutation]
  );

  const markSaved = useCallback(() => {
    const now = new Date();
    setLastSavedAt(now.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }));
  }, []);

  const handleOpenAddModule = useCallback(() => {
    setIsAddModuleModalOpen(true);
  }, []);

  const handleCloseAddModule = useCallback(() => {
    if (isCreatingModernizedModule) return;
    setIsAddModuleModalOpen(false);
  }, [isCreatingModernizedModule]);

  const handleSaveModernizedModule = useCallback(
    async ({ title, description }: ModernizerAddModuleInput) => {
      try {
        const result = await createModernizedModuleMutation({
          variables: {
            courseId: review.courseId,
            title,
            description,
          },
          refetchQueries: [
            {
              query: GetModernizedCourseDocument,
              variables: { courseId: review.courseId },
            },
          ],
          awaitRefetchQueries: true,
        });

        const payload = result.data?.createModernizedModule;
        if (!payload?.success || !payload.module?.id) {
          throw new Error(payload?.message ?? "Failed to create module");
        }

        if (!isMountedRef.current) return;

        setPendingCreatedModuleId(payload.module.id);
        setIsAddModuleModalOpen(false);
        markSaved();
        showToast({
          message: "Module added to the modernized course.",
          type: "success",
        });
      } catch (error) {
        if (!isMountedRef.current) return;
        showToast({
          message: getErrorMessage(error, "Failed to add module. Please try again."),
          type: "error",
        });
      }
    },
    [createModernizedModuleMutation, markSaved, review.courseId, showToast]
  );

  const handleSaveEdit = useCallback(
    async (item: CourseEvaluatorReviewSidebarItem, editedFields: StructuredFields) => {
      if (decisionsRef.current.get(item.id)?.decision === "synced") return;

      const variables = getEditMutationVariables(item, editedFields);
      if (!variables) return;

      setIsSaving(true);
      try {
        const result = await updateModernizedActivityFieldsMutation({
          variables,
          update: (cache) => {
            const cacheId = cache.identify({
              __typename: getActivityCacheTypename(item.entityType),
              id: item.id,
            });
            if (!cacheId) return;

            const fields: Record<string, () => unknown> = {};
            if (variables.recommendedTitle !== undefined) {
              fields.recommendedTitle = () => variables.recommendedTitle;
            }
            if (variables.recommendedDescription !== undefined) {
              fields.recommendedDescription = () => variables.recommendedDescription;
            }
            if (variables.recommendedPointsPossible !== undefined) {
              fields.recommendedPointsPossible = () => variables.recommendedPointsPossible;
            }
            if (variables.recommendedDueDate !== undefined) {
              fields.recommendedDueDate = () => variables.recommendedDueDate;
              fields.recommendedDueDateCleared = () => variables.recommendedDueDate === null;
            }
            if (variables.recommendedValue !== undefined) {
              fields.recommendedValue = () => variables.recommendedValue;
            }
            cache.modify({ id: cacheId, fields });
          },
        });
        if (!result.data?.updateModernizedActivityFields.success) {
          throw new Error("updateModernizedActivityFields mutation failed");
        }
        if (!isMountedRef.current) return;

        setLiveActivityDetails((prev) => {
          const current = prev[item.id];
          if (!current) return prev;

          // Only treat as cleared when the field is explicitly a string (present
          // in the form) but empty — not when undefined (field was never shown).
          const dueDateCleared = typeof editedFields.dueDate === "string" && !editedFields.dueDate.trim();
          return {
            ...prev,
            [item.id]: {
              ...current,
              recommendedTitle: editedFields.title,
              recommendedDescription: editedFields.description,
              recommendedPointsPossible: editedFields.points,
              recommendedDueDate: dueDateCleared ? undefined : editedFields.dueDate,
              recommendedDueDateCleared: dueDateCleared || undefined,
            },
          };
        });
        markSaved();
      } catch {
        if (!isMountedRef.current) return;
      } finally {
        if (isMountedRef.current) {
          setIsSaving(false);
        }
      }
    },
    [markSaved, updateModernizedActivityFieldsMutation]
  );

  const handleOpenIleCreation = useCallback((item: CourseEvaluatorReviewSidebarItem) => {
    if (!isIleRecommendationItem(item)) return;
    setIleCreationItemId(item.id);
  }, []);

  const handleCloseIleCreation = useCallback(() => {
    if (isCreatingIle || isUpdatingIle) return;
    setIleCreationItemId(null);
  }, [isCreatingIle, isUpdatingIle]);

  const handleSaveIleCreation = useCallback(
    async (activityData: ActivityDataInput) => {
      if (!ileCreationItemId) return;

      try {
        const currentDetail = liveActivityDetails[ileCreationItemId];
        const boundIleId = currentDetail?.createdIleId;
        const savedIle = boundIleId
          ? await (async () => {
              const result = await updateAiTutorMutation({
                variables: {
                  id: boundIleId,
                  aiTutor: mapActivityDataToUpdateIleInput(activityData),
                },
              });
              const payload = result.data?.updateAITutor;
              const updatedIle = payload?.data?.[0];
              if (!payload?.success || !updatedIle?.id) {
                throw new Error(payload?.message || "Failed to update ILE");
              }
              return {
                id: updatedIle.id,
                title: activityData.title ?? updatedIle.title,
                description: activityData.description ?? updatedIle.description,
                dueDate: normalizeDueDate(activityData.dueDate),
                targetAudience: activityData.targetAudience ?? null,
                activityState: activityData.activityState ?? null,
                scheduledDate: activityData.scheduledDate ?? null,
                objectives: activityData.objectives ?? activityData.rawObjectives ?? [],
              };
            })()
          : await (async () => {
              const result = await createIleForModernizedRecommendationMutation({
                variables: {
                  courseId: review.courseId,
                  recommendationId: ileCreationItemId,
                  ile: mapActivityDataToCreateIleInput(activityData),
                },
              });

              const payload = result.data?.createIleForModernizedRecommendation;
              if (!payload?.success || !payload.ile?.id) {
                throw new Error(payload?.message || "Failed to create ILE");
              }

              if (isMountedRef.current) {
                setLiveActivityDetails((prev) => {
                  const current = prev[ileCreationItemId];
                  if (!current) return prev;

                  return {
                    ...prev,
                    [ileCreationItemId]: {
                      ...current,
                      createdIleId: payload.ile.id,
                      createdIleWasUserCreated: true,
                    },
                  };
                });
              }

              const updateResult = await updateAiTutorMutation({
                variables: {
                  id: payload.ile.id,
                  aiTutor: mapActivityDataToUpdateIleInput(activityData),
                },
              });
              const updatePayload = updateResult.data?.updateAITutor;
              if (!updatePayload?.success) {
                throw new Error(updatePayload?.message || "Failed to save ILE details");
              }

              return {
                id: payload.ile.id,
                title: activityData.title ?? payload.ile.title,
                description: activityData.description ?? payload.ile.description,
                dueDate: normalizeDueDate(activityData.dueDate),
                targetAudience: activityData.targetAudience ?? payload.ile.targetAudience ?? null,
                activityState: activityData.activityState ?? payload.ile.activityState ?? null,
                scheduledDate: activityData.scheduledDate ?? payload.ile.scheduledDate ?? null,
                objectives: activityData.objectives ?? activityData.rawObjectives ?? [],
              };
            })();

        if (!isMountedRef.current) return;

        setLiveActivityDetails((prev) => {
          const current = prev[ileCreationItemId];
          if (!current) return prev;

          return {
            ...prev,
            [ileCreationItemId]: {
              ...current,
              createdIleId: savedIle.id,
              createdIleWasUserCreated: current.createdIleWasUserCreated ?? !boundIleId,
              createdIleTitle: savedIle.title,
              createdIleDescription: savedIle.description,
              createdIleContext: activityData.context ?? null,
              createdIleDueDate: savedIle.dueDate,
              createdIleTargetAudience: savedIle.targetAudience,
              createdIleActivityState: savedIle.activityState,
              createdIleScheduledDate:
                savedIle.scheduledDate instanceof Date ? savedIle.scheduledDate.toISOString() : savedIle.scheduledDate,
              createdIleObjectives: savedIle.objectives.map((objective, index) => ({
                id: objective.id,
                objective: objective.objective,
                sequence: objective.sequence ?? index + 1,
                evalCriteria: objective.evalCriteria ?? [],
              })),
            },
          };
        });
        setIleCreationItemId(null);
        markSaved();
      } catch (error) {
        if (!isMountedRef.current) return;
        showToast({
          message: getErrorMessage(error, "Failed to save the ILE. Please try again."),
          type: "error",
        });
      }
    },
    [
      createIleForModernizedRecommendationMutation,
      ileCreationItemId,
      liveActivityDetails,
      markSaved,
      showToast,
      updateAiTutorMutation,
    ]
  );

  // --- Actions ---
  const makeDecision = useCallback(
    async (item: CourseEvaluatorReviewSidebarItem, decision: CourseEvaluatorReviewDecision) => {
      const itemId = item.id;
      const previous = decisionsRef.current.get(itemId) ?? { decision: null };

      setUndoEntry({ itemId, previous });
      setDecisions((prev) => {
        const next = new Map(prev);
        next.set(itemId, { decision });
        return next;
      });

      // Only auto-navigate if we're viewing a specific item (not on overview)
      // This keeps the Gap Analysis drawer open when making decisions from overview
      if (selectedItemId !== null) {
        advanceToNextUndecided(itemId);
      }

      setIsSaving(true);
      try {
        await persistDecision(item, decision);
        if (!isMountedRef.current) return;
        markSaved();
      } catch {
        if (!isMountedRef.current) return;
        setDecisions((prev) => {
          const next = new Map(prev);
          if (previous.decision === null) {
            next.delete(itemId);
          } else {
            next.set(itemId, previous);
          }
          return next;
        });
        setUndoEntry(null);
      } finally {
        if (isMountedRef.current) {
          setIsSaving(false);
        }
      }
    },
    [selectedItemId, advanceToNextUndecided, persistDecision, markSaved]
  );

  const handleAccept = useCallback(
    (item: CourseEvaluatorReviewSidebarItem) => {
      const detail = liveActivityDetails[item.id];
      if (isExistingImportedIleItem(item, detail)) {
        return;
      }
      if (isIleRecommendationItem(item) && !detail?.createdIleId) {
        setIleCreationItemId(item.id);
        return;
      }
      void makeDecision(item, "accepted");
    },
    [liveActivityDetails, makeDecision]
  );

  const handleReject = useCallback(
    (item: CourseEvaluatorReviewSidebarItem) => {
      void makeDecision(item, "rejected");
    },
    [makeDecision]
  );

  const makeBulkDecision = useCallback(
    async (items: CourseEvaluatorReviewSidebarItem[], decision: "accepted" | "rejected") => {
      const currentDecisions = decisionsRef.current;
      const existingImportedIleItemIds =
        decision === "accepted"
          ? new Set(
              items
                .filter((item) => isExistingImportedIleItem(item, liveActivityDetails[item.id]))
                .map((item) => item.id)
            )
          : new Set<string>();
      const missingIleItems =
        decision === "accepted"
          ? items.filter(
              (item) =>
                (currentDecisions.get(item.id)?.decision ?? null) === null &&
                isIleRecommendationItem(item) &&
                !existingImportedIleItemIds.has(item.id) &&
                !liveActivityDetails[item.id]?.createdIleId
            )
          : [];
      const missingIleItemIds = new Set(missingIleItems.map((item) => item.id));
      const actionableItems = items.filter((item) => {
        const currentDecision = currentDecisions.get(item.id)?.decision ?? null;
        if (currentDecision !== null) return false;
        if (existingImportedIleItemIds.has(item.id)) return false;
        return !missingIleItemIds.has(item.id);
      });

      if (actionableItems.length === 0) {
        if (missingIleItems.length > 0) {
          showToast({
            message: getMissingIleBulkWarningMessage(missingIleItems.length),
            type: "warning",
          });
        }
        return;
      }

      const previousDecisions = new Map<string, DecisionEntry>();
      actionableItems.forEach((item) => {
        previousDecisions.set(item.id, currentDecisions.get(item.id) ?? { decision: null });
      });

      setUndoEntry(null);
      setDecisions((prev) => {
        const next = new Map(prev);
        actionableItems.forEach((item) => next.set(item.id, { decision }));
        return next;
      });

      setIsSaving(true);
      try {
        const failedItemIds = new Set<string>();
        for (const item of actionableItems) {
          try {
            await persistDecision(item, decision);
          } catch {
            failedItemIds.add(item.id);
          }
        }
        if (!isMountedRef.current) return;

        if (failedItemIds.size > 0) {
          setDecisions((prev) => {
            const next = new Map(prev);
            failedItemIds.forEach((itemId) => {
              const previous = previousDecisions.get(itemId);
              if (!previous || previous.decision === null) {
                next.delete(itemId);
              } else {
                next.set(itemId, previous);
              }
            });
            return next;
          });

          showToast({
            message: `Could not update ${failedItemIds.size} ${
              failedItemIds.size === 1 ? "activity" : "activities"
            }. Please try again.`,
            type: "error",
          });
        }

        if (actionableItems.length > failedItemIds.size) {
          markSaved();
        }

        if (missingIleItems.length > 0) {
          showToast({
            message: getMissingIleBulkWarningMessage(missingIleItems.length),
            type: "warning",
          });
        }
      } finally {
        if (isMountedRef.current) {
          setIsSaving(false);
        }
      }
    },
    [liveActivityDetails, markSaved, persistDecision, showToast]
  );

  const getSourceGroupItems = useCallback(
    (group: CourseEvaluatorReviewSidebarGroup) =>
      review.sidebarGroups.find((sourceGroup) => sourceGroup.id === group.id)?.items ?? group.items,
    [review.sidebarGroups]
  );

  const handleAcceptGroup = useCallback(
    (group: CourseEvaluatorReviewSidebarGroup) => {
      void makeBulkDecision(getSourceGroupItems(group), "accepted");
    },
    [getSourceGroupItems, makeBulkDecision]
  );

  const handleRejectGroup = useCallback(
    (group: CourseEvaluatorReviewSidebarGroup) => {
      void makeBulkDecision(getSourceGroupItems(group), "rejected");
    },
    [getSourceGroupItems, makeBulkDecision]
  );

  const handleUndo = useCallback(async () => {
    if (!undoEntry) return;
    const item = allItems.find((candidate) => candidate.id === undoEntry.itemId);
    if (!item) return;
    const current = decisions.get(undoEntry.itemId) ?? { decision: null };

    setDecisions((prev) => {
      const next = new Map(prev);
      if (undoEntry.previous.decision === null) {
        next.delete(undoEntry.itemId);
      } else {
        next.set(undoEntry.itemId, undoEntry.previous);
      }
      return next;
    });
    setSelectedItemForTriage(undoEntry.itemId);
    setUndoEntry(null);

    setIsSaving(true);
    try {
      await persistDecision(item, undoEntry.previous.decision);
      if (!isMountedRef.current) return;
      markSaved();
    } catch {
      if (!isMountedRef.current) return;
      setDecisions((prev) => {
        const next = new Map(prev);
        if (current.decision === null) {
          next.delete(undoEntry.itemId);
        } else {
          next.set(undoEntry.itemId, current);
        }
        return next;
      });
      setUndoEntry(undoEntry);
    } finally {
      if (isMountedRef.current) {
        setIsSaving(false);
      }
    }
  }, [undoEntry, allItems, decisions, persistDecision, markSaved]);

  const handleNavigateNext = useCallback(() => {
    if (!selectedItemId) {
      if (orderedIds.length > 0) setSelectedItemForTriage(orderedIds[0]);
      return;
    }
    const idx = orderedIds.indexOf(selectedItemId);
    if (idx < orderedIds.length - 1) setSelectedItemForTriage(orderedIds[idx + 1]);
  }, [selectedItemId, orderedIds, setSelectedItemForTriage]);

  const handleNavigatePrev = useCallback(() => {
    if (!selectedItemId) return;
    const idx = orderedIds.indexOf(selectedItemId);
    if (idx > 0) setSelectedItemForTriage(orderedIds[idx - 1]);
    else setSelectedItemForTriage(null);
  }, [selectedItemId, orderedIds, setSelectedItemForTriage]);

  const handleJumpToEntity = useCallback(
    (entityId: string, dimension: string) => {
      setSelectedDimension(dimension as TriageDimension);
      setSelectedItemForTriage(entityId, "push");
    },
    [setSelectedItemForTriage]
  );

  // --- Publish flow ---
  const handleOpenPublishSheet = useCallback(() => {
    if (queuedPublishItems.length === 0) return;
    setPublishStatus("idle");
    setLastPublishRecord(null);
    setLastPublishedFallbackItems([]);
    setIsPublishHistoryOpen(false);
    setIsPublishSheetOpen(true);
  }, [queuedPublishItems.length]);

  const handleClosePublishSheet = useCallback(() => {
    if (publishStatus === "publishing") return;
    setIsPublishSheetOpen(false);
  }, [publishStatus]);

  /** Closes the success state and returns the review page to its normal view. */
  const handleDonePublish = useCallback(() => {
    setIsPublishSheetOpen(false);
    setPublishStatus("idle");
    setLastPublishRecord(null);
    setLastPublishedFallbackItems([]);
  }, []);

  const handleOpenPublishHistory = useCallback(() => {
    setIsPublishSheetOpen(false);
    setIsPublishHistoryOpen(true);
  }, []);

  const handleClosePublishHistory = useCallback(() => {
    setIsPublishHistoryOpen(false);
  }, []);

  const handleClearPublishHistory = useCallback(async () => {
    try {
      const result = await clearModernizedCoursePublishHistoryMutation({
        variables: { courseId: review.courseId },
        refetchQueries: [
          {
            query: GetModernizedCoursePublishHistoryDocument,
            variables: { courseId: review.courseId },
          },
        ],
        awaitRefetchQueries: true,
      });

      if (!result.data?.clearModernizedCoursePublishHistory.success) {
        throw new Error("clearModernizedCoursePublishHistory mutation failed");
      }
    } catch {
      if (!isMountedRef.current) return;
    }
  }, [clearModernizedCoursePublishHistoryMutation, review.courseId]);

  const markItemsAsPublished = useCallback((itemsToMark: QueuedPublishItem[]) => {
    const publishedIds = new Set(itemsToMark.map((item) => item.id));
    setDecisions((prev) => {
      const next = new Map(prev);
      itemsToMark.forEach((item) => {
        if (publishedIds.has(item.id)) {
          next.set(item.id, { decision: "synced" });
        }
      });
      return next;
    });
    setLastPublishedFallbackItems(
      itemsToMark.map((item) => ({
        id: item.id,
        title: item.title,
        category: item.category,
        moduleName: item.moduleName,
      }))
    );
  }, []);

  const finishPublish = useCallback((publishRecord: PublishHistoryRecord | null) => {
    setLastPublishRecord(publishRecord);
    setPublishStatus("done");
  }, []);

  const lastPublishedItems = useMemo<PublishedPublishItem[]>(
    () => lastPublishRecord?.autoPublishedItems.map(mapPublishHistoryItem) ?? lastPublishedFallbackItems,
    [lastPublishedFallbackItems, lastPublishRecord]
  );

  const lastPublishManualSteps = useMemo<PublishManualStep[]>(
    () => lastPublishRecord?.manualSteps.map(mapPublishHistoryManualStep) ?? [],
    [lastPublishRecord]
  );

  const lastPublishDocumentUpdates = useMemo<PublishDocumentUpdate[]>(
    () => (lastPublishRecord ? review.documentUpdates : []),
    [lastPublishRecord, review.documentUpdates]
  );

  const hasLiveItems = useMemo(
    () => queuedPublishItems.some((item) => item.externalVisibility === ExternalVisibility.Published),
    [queuedPublishItems]
  );

  const executePublish = useCallback(async () => {
    if (queuedPublishItems.length === 0 || !review.canPublishToLms) return;
    const snapshot = [...queuedPublishItems];
    setPublishStatus("publishing");
    try {
      const mutationResult = await publishModernizedActivitiesMutation({
        variables: {
          courseId: review.courseId,
          itemIds: snapshot.map((item) => item.id),
        },
        update: (cache, result) => {
          if (result.data?.publishModernizedActivities.success !== true) {
            return;
          }

          snapshot.forEach((item) => updateActivityStatusInCache(cache, item, ModernizedActivityStatus.Published));
        },
      });
      if (!isMountedRef.current) return;
      const publishResult = mutationResult.data?.publishModernizedActivities;
      if (!publishResult?.success || !publishResult.publishEventId) {
        throw new Error("publishModernizedActivities mutation failed");
      }

      const publishedIds = new Set(publishResult.items.map((item) => item.id));
      if (publishedIds.size !== snapshot.length) {
        throw new Error("publishModernizedActivities returned partial item results");
      }

      const publishedSnapshot = snapshot.filter((item) => publishedIds.has(item.id));
      markItemsAsPublished(publishedSnapshot);

      let publishRecord: PublishHistoryRecord | null = null;
      try {
        const historyResult = await refetchPublishHistory({ courseId: review.courseId });
        if (!isMountedRef.current) return;
        const publishHistoryResult = historyResult.data?.getModernizedCoursePublishHistory;
        publishRecord =
          publishHistoryResult?.success === true
            ? (publishHistoryResult.records.find((record) => record.id === publishResult.publishEventId) ?? null)
            : null;
      } catch {
        publishRecord = null;
      }

      if (!isMountedRef.current) return;
      if (!publishRecord) {
        finishPublish(null);
        return;
      }

      finishPublish(publishRecord);
    } catch {
      if (!isMountedRef.current) return;
      setPublishStatus("error");
    }
  }, [
    finishPublish,
    markItemsAsPublished,
    publishModernizedActivitiesMutation,
    queuedPublishItems,
    refetchPublishHistory,
    review.canPublishToLms,
    review.courseId,
  ]);

  const handlePublish = useCallback(() => {
    if (hasLiveItems) {
      setIsLivePublishWarningVisible(true);
      return;
    }
    void executePublish();
  }, [hasLiveItems, executePublish]);

  const handleConfirmLivePublish = useCallback(() => {
    setIsLivePublishWarningVisible(false);
    void executePublish();
  }, [executePublish]);

  const handleCancelLivePublish = useCallback(() => {
    setIsLivePublishWarningVisible(false);
  }, []);

  const handleExportPdf = useCallback(async () => {
    setIsExportingPdf(true);
    try {
      const result = await requestModernizedCourseExportMutation({
        variables: {
          courseId: review.courseId,
          format: ModernizedCourseExportFormat.Pdf,
        },
      });
      const exportResult = result.data?.requestModernizedCourseExport;
      if (!exportResult?.success || !exportResult.downloadUrl) {
        throw new Error(exportResult?.message ?? "Failed to generate PDF export");
      }

      if (typeof window !== "undefined") {
        const managedFileAction = getManagedFileDownloadAction(exportResult.downloadUrl);
        if (managedFileAction) {
          await submitAuthenticatedHiddenDownloadForm(managedFileAction, `modernized-course-pdf-export-${Date.now()}`, {
            disposition: "attachment",
          });
        } else {
          window.open(exportResult.downloadUrl, "_blank", "noopener,noreferrer");
        }
      }
    } catch (error) {
      showToast({
        message: error instanceof Error ? error.message : "Failed to generate PDF export",
        type: "error",
      });
    } finally {
      if (isMountedRef.current) {
        setIsExportingPdf(false);
      }
    }
  }, [requestModernizedCourseExportMutation, review.courseId, showToast]);

  const handleExportImscc = useCallback(async () => {
    setIsExportingImscc(true);
    try {
      const result = await requestModernizedCourseExportMutation({
        variables: {
          courseId: review.courseId,
          format: ModernizedCourseExportFormat.Imscc,
        },
      });
      const exportResult = result.data?.requestModernizedCourseExport;
      if (!exportResult?.success || !exportResult.downloadUrl) {
        throw new Error(exportResult?.message ?? "Failed to generate Common Cartridge export");
      }

      if (typeof window !== "undefined") {
        const managedFileAction = getManagedFileDownloadAction(exportResult.downloadUrl);
        if (managedFileAction) {
          await submitAuthenticatedHiddenDownloadForm(
            managedFileAction,
            `modernized-course-imscc-export-${Date.now()}`,
            {
              disposition: "attachment",
            }
          );
        } else {
          window.open(exportResult.downloadUrl, "_blank", "noopener,noreferrer");
        }
      }
    } catch (error) {
      showToast({
        message: error instanceof Error ? error.message : "Failed to generate Common Cartridge export",
        type: "error",
      });
    } finally {
      if (isMountedRef.current) {
        setIsExportingImscc(false);
      }
    }
  }, [requestModernizedCourseExportMutation, review.courseId, showToast]);

  const toggleSection = (sectionId: string) => {
    setCollapsedSectionIds((previous) => {
      const next = new Set(previous);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  return (
    <>
      <ReviewPanel
        review={liveReview}
        activityDetails={liveActivityDetails}
        selectedItemId={selectedItemId}
        selectedReviewItem={selectedSidebarItem}
        selectedDecision={selectedDecision}
        filterMode={filterMode}
        collapsedSectionIds={collapsedSectionIds}
        reviewedProgress={reviewedProgress}
        getDecision={getDecision}
        lastSavedAt={lastSavedAt}
        isSaving={isSaving}
        onExitReview={onExitReview}
        onSelectItem={setSelectedItemForTriage}
        onToggleSection={toggleSection}
        onFilterModeChange={setFilterMode}
        onStartReviewing={() => {
          // Select first undecided item
          const firstUndecided = orderedIds.find((id) => !decisions.get(id)?.decision);
          if (firstUndecided) setSelectedItemForTriage(firstUndecided);
        }}
        onAccept={handleAccept}
        onReject={handleReject}
        onAcceptGroup={handleAcceptGroup}
        onRejectGroup={handleRejectGroup}
        onUndo={undoEntry ? handleUndo : undefined}
        onSaveEdit={handleSaveEdit}
        onAddModule={handleOpenAddModule}
        onCreateIle={handleOpenIleCreation}
        onNavigateNext={handleNavigateNext}
        onNavigatePrev={handleNavigatePrev}
        onJumpToEntity={handleJumpToEntity}
        canAcceptSelectedItem={canAcceptSelectedItem}
        acceptDisabledReason={acceptDisabledReason}
        acceptHiddenReason={acceptHiddenReason}
        queuedPublishItems={queuedPublishItems}
        lastPublishedItems={lastPublishedItems}
        publishManualSteps={lastPublishManualSteps}
        publishDocumentUpdates={lastPublishDocumentUpdates}
        undecidedCount={stats.total - stats.decided}
        isPublishSheetOpen={isPublishSheetOpen}
        publishStatus={publishStatus}
        onOpenPublishSheet={handleOpenPublishSheet}
        onClosePublishSheet={handleClosePublishSheet}
        onDonePublish={handleDonePublish}
        onPublish={handlePublish}
        onExportPdf={handleExportPdf}
        onExportImscc={handleExportImscc}
        isExportingPdf={isExportingPdf}
        isExportingImscc={isExportingImscc}
        publishHistoryRecords={publishHistoryRecords}
        pendingManualStepsCount={pendingManualStepsCount}
        isPublishHistoryOpen={isPublishHistoryOpen}
        isClearingPublishHistory={isClearingPublishHistory}
        onOpenPublishHistory={handleOpenPublishHistory}
        onClosePublishHistory={handleClosePublishHistory}
        onClearPublishHistory={handleClearPublishHistory}
      />
      <ModernizerIleCreationSheet
        open={Boolean(ileCreationItemId)}
        initialValues={ileCreationInitialValues}
        isSubmitting={isCreatingIle || isUpdatingIle}
        onSave={handleSaveIleCreation}
        onClose={handleCloseIleCreation}
      />
      <ModernizerAddModuleModal
        open={isAddModuleModalOpen}
        isSubmitting={isCreatingModernizedModule}
        onSave={handleSaveModernizedModule}
        onClose={handleCloseAddModule}
      />
      <CustomAdminModal
        visible={isLivePublishWarningVisible}
        onRequestClose={handleCancelLivePublish}
        title="Publishing to a Live Course"
        ariaLabel="Live Course Publish Warning">
        <View style={styles.warningModalContent}>
          <CustomText
            text={`This course is currently published and visible to enrolled students in ${review.sourceLabel}. Any changes you publish will go live immediately and students will see them right away.`}
            useTranslationText={false}
            size="s"
            style={styles.warningModalText}
          />
          <View style={styles.warningModalButtons}>
            <Pressable
              onPress={handleCancelLivePublish}
              style={styles.warningModalCancelButton}
              accessibilityRole="button">
              <CustomText
                text="Back"
                useTranslationText={false}
                size="s"
                weight="medium"
                style={styles.warningModalCancelText}
              />
            </Pressable>
            <Pressable
              onPress={handleConfirmLivePublish}
              style={styles.warningModalConfirmButton}
              accessibilityRole="button">
              <CustomText
                text="Publish Anyway"
                useTranslationText={false}
                size="s"
                weight="medium"
                style={styles.warningModalConfirmText}
              />
            </Pressable>
          </View>
        </View>
      </CustomAdminModal>
    </>
  );
}

const styles = StyleSheet.create({
  warningModalContent: {
    padding: 24,
    gap: 24,
  },
  warningModalText: {
    color: "#344054",
    lineHeight: 22,
  },
  warningModalButtons: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 12,
  },
  warningModalCancelButton: {
    paddingVertical: 10,
    paddingHorizontal: 18,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#D0D5DD",
    backgroundColor: "#FFFFFF",
  },
  warningModalCancelText: {
    color: "#344054",
  },
  warningModalConfirmButton: {
    paddingVertical: 10,
    paddingHorizontal: 18,
    borderRadius: 8,
    backgroundColor: "#B42318",
  },
  warningModalConfirmText: {
    color: "#FFFFFF",
  },
});

export default CourseEvaluatorReviewContainer;
